/**
 * Secure Access → Access Devices
 *
 * The enrolment/posture control surface for end-user devices. The lifecycle is
 * enforced end-to-end on the gateway (an un-approved device's certificate is
 * refused; a revoked one is dropped fleet-wide in seconds) — this page is what
 * lets an admin drive it without curl.
 *
 * Lifecycle: enrol → pending_approval → (approve) → active → (revoke) → revoked.
 * There is no server-side "retire" transition, so it is not offered here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw, Shield, CheckCircle2, XCircle, Clock, Trash2, ShieldCheck, ShieldAlert, UserCog, Users, KeyRound } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ZtnaChip from '../../components/access/ZtnaChip';
import { DeviceDetailModal, ReassignDeviceModal, RevokeDeviceModal } from '../../components/access/DeviceModals';
import {
  deviceStatusLabel as statusLabel,
  describeSubjects,
  deviceSubjects,
  sharedSubjects,
  fmtDate as fmt,
  postureFacts,
  tokenBindingDisplay,
} from '../../utils/accessDevice';
import { useToast } from '../../components/common/ToastContainer';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import accessService, {
  type AccessDevice,
  type AccessDeviceStatus,
} from '../../services/access.service';

/**
 * Tailwind classes per binding tone. A lookup rather than a template string so
 * the class names survive Tailwind's static extraction — a constructed
 * `text-${tone}-600` is not in the built stylesheet and renders unstyled.
 */
const BINDING_TONE: Record<string, string> = {
  ok: 'text-teal-700',
  info: 'text-gray-600',
  warn: 'text-warning-600',
  danger: 'text-danger-600',
  muted: 'text-gray-400',
};

const AccessDevices = () => {
  const toast = useToast();
  // Reassignment is tenant administration, not self-service: it revokes a
  // certificate and moves ownership. The server enforces this too — this only
  // keeps the button from being offered to someone who would be refused.
  const user = useAuthStore((state) => state.user);
  const canReassign =
    user?.type === USER_TYPES.SUPER_ADMIN || user?.type === USER_TYPES.TENANT_ADMIN;

  const [devices, setDevices] = useState<AccessDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AccessDeviceStatus | 'all'>('all');

  const [approveTarget, setApproveTarget] = useState<AccessDevice | null>(null);
  const [approving, setApproving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccessDevice | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AccessDevice | null>(null);
  const [reassignTarget, setReassignTarget] = useState<AccessDevice | null>(null);
  const [detailTarget, setDetailTarget] = useState<AccessDevice | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await accessService.listDevices({ limit: 200 });
      setDevices(result?.items ?? []);
    } catch (err) {
      // Surfaced, not swallowed: a SuperAdmin with no tenant selected 400s here,
      // and a silent empty table would hide the reason.
      setError(err instanceof Error ? err.message : 'Failed to load devices');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  const counts = useMemo(
    () => ({
      total: devices.length,
      pending: devices.filter((d) => d.status === 'pending_approval').length,
      active: devices.filter((d) => d.status === 'active').length,
      revoked: devices.filter((d) => d.status === 'revoked').length,
    }),
    [devices],
  );

  // Default the view to Pending approval the first time any exist — that is the
  // action an admin came here to take.
  const [autoFocused, setAutoFocused] = useState(false);
  useEffect(() => {
    if (!autoFocused && counts.pending > 0) {
      setStatusFilter('pending_approval');
      setAutoFocused(true);
    }
  }, [autoFocused, counts.pending]);

  const visible = useMemo(
    () => (statusFilter === 'all' ? devices : devices.filter((d) => d.status === statusFilter)),
    [devices, statusFilter],
  );

  const handleApprove = async () => {
    if (!approveTarget) return;
    try {
      setApproving(true);
      await accessService.approveDevice(approveTarget.id);
      toast.success('Device approved', `${approveTarget.device_id} — the roster republishes automatically`);
      setApproveTarget(null);
      await fetchDevices();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the device');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteDevice(deleteTarget.id);
      toast.success('Device deleted', deleteTarget.device_id);
      setDeleteTarget(null);
      await fetchDevices();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the device');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<AccessDevice>[] = [
    {
      key: 'device_id',
      label: 'Device',
      sortable: true,
      render: (d) => (
        <button
          type="button"
          className="text-left"
          onClick={() => setDetailTarget(d)}
          title="View device detail"
        >
          <div className="font-medium text-gray-900 hover:text-primary-600">{d.device_name || d.device_id}</div>
          <div className="text-xs font-mono text-gray-500">{d.device_id}</div>
        </button>
      ),
    },
    {
      key: 'user_email',
      label: 'Owner',
      sortable: true,
      // Owner is really "primary owner, plus whoever else may present this
      // device" — a device can be shared (design 23 §9.4 decision D), and an
      // admin who cannot see that a laptop carries a second identity cannot
      // audit it. Only the EXCEPTIONS are called out: an ordinary device is
      // seeded with the owner's sub AND email, so a raw subject count would
      // read "2 subjects" on every normal row and mean nothing by it. The
      // tooltip carries the full list either way.
      render: (d) => {
        const shared = sharedSubjects(d);
        const all = deviceSubjects(d);
        return (
          <div>
            <div className="text-gray-900">
              {d.user_email || <span className="text-gray-400">— unassigned</span>}
            </div>
            {all.length === 0 ? (
              <span
                className="mt-0.5 inline-flex items-center gap-1 text-xs text-warning-600"
                title={describeSubjects(all)}
              >
                <Users className="w-3.5 h-3.5" /> no subjects
              </span>
            ) : shared.length > 0 ? (
              <span
                className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary-600"
                title={describeSubjects(all)}
              >
                <Users className="w-3.5 h-3.5" />
                shared with {shared.length} other{shared.length !== 1 ? 's' : ''}
              </span>
            ) : null}
          </div>
        );
      },
    },
    { key: 'platform', label: 'Platform', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (d) => <StatusBadge status={statusLabel(d.status)} />,
    },
    {
      key: 'posture',
      label: 'Posture',
      render: (d) => {
        const facts = postureFacts(d.posture);
        if (facts.length === 0) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400" title="No verified posture yet">
              <ShieldAlert className="w-3.5 h-3.5" /> none
            </span>
          );
        }
        return (
          <span
            className="inline-flex items-center gap-1 text-xs text-teal-700"
            title={facts.map(([k, v]) => `${k}=${v}`).join('\n')}
          >
            <ShieldCheck className="w-3.5 h-3.5" /> {facts.length} fact{facts.length !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      key: 'cert',
      label: 'Certificate',
      render: (d) =>
        d.certificate_serial_number ? (
          <div className="text-xs">
            <div className="font-mono text-gray-600">{d.certificate_serial_number.slice(0, 12)}…</div>
            <div className="text-gray-400">exp {fmt(d.certificate_expires_at)}</div>
          </div>
        ) : (
          <span className="text-gray-400 text-xs">not issued</span>
        ),
    },
    {
      // The LAST binding result, not a live one — the gateway decides this at
      // admission and the orchestrator stores what it reported (WP8B, design
      // 23 §3.1). It is next to Last Seen on purpose: the two come from the
      // same session record, and a binding result without the time it was
      // observed invites reading a stale value as current.
      key: 'binding',
      label: 'Token Binding',
      render: (d) => {
        const b = tokenBindingDisplay(d.last_token_binding);
        return (
          <span
            className={`inline-flex items-center gap-1 text-xs ${BINDING_TONE[b.tone] ?? BINDING_TONE.muted}`}
            title={`${b.detail}${d.last_token_binding_at ? `\n\nObserved ${fmt(d.last_token_binding_at)}` : ''}`}
            data-testid="device-token-binding"
          >
            <KeyRound className="w-3.5 h-3.5" /> {b.label}
          </span>
        );
      },
    },
    { key: 'last_seen', label: 'Last Seen', sortable: true, render: (d) => <span className="text-sm text-gray-600">{fmt(d.last_seen)}</span> },
    {
      key: 'actions',
      label: '',
      render: (d) => (
        <div className="flex items-center justify-end gap-1">
          {/* The device's own activity, pre-filtered (design §7.5): the CN is
              what the session rows carry, and `device_id` IS the CN here — the
              orchestrator resolves it to the device record on ingest. */}
          <Link
            to={`/access/activity?device=${encodeURIComponent(d.device_id)}`}
            className="rounded p-1.5 text-gray-400 hover:bg-primary-50 hover:text-primary-600"
            title="View this device's access activity"
            data-testid="device-activity-link"
          >
            <Activity className="w-4 h-4" />
          </Link>
          {d.status === 'pending_approval' && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-success-50 text-success-600"
              title="Approve device"
              onClick={() => setApproveTarget(d)}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          {d.status === 'active' && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-danger-50 text-danger-600"
              title="Revoke device"
              onClick={() => setRevokeTarget(d)}
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
          {canReassign && d.status !== 'revoked' && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-warning-50 text-gray-400 hover:text-warning-600"
              title="Reassign to another owner (revokes the certificate)"
              data-testid="device-reassign"
              onClick={() => setReassignTarget(d)}
            >
              <UserCog className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-danger-600"
            title="Delete device record"
            onClick={() => setDeleteTarget(d)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const filters: { key: AccessDeviceStatus | 'all'; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: counts.total },
    { key: 'pending_approval', label: 'Pending approval', count: counts.pending },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'revoked', label: 'Revoked', count: counts.revoked },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Access Devices</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">
            Enrolled end-user devices — approve enrolments, review posture, and revoke access.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => void fetchDevices()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <StatCard title="Devices" value={counts.total} icon={Shield} color="primary" loading={loading} />
        <StatCard title="Pending Approval" value={counts.pending} icon={Clock} color={counts.pending > 0 ? 'warning' : 'secondary'} loading={loading} />
        <StatCard title="Active" value={counts.active} icon={CheckCircle2} color="success" loading={loading} />
        <StatCard title="Revoked" value={counts.revoked} icon={XCircle} color="danger" loading={loading} />
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              statusFilter === f.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
            }`}
          >
            {f.label}
            {typeof f.count === 'number' && (
              <span
                className={`ml-1.5 text-xs ${statusFilter === f.key ? 'text-white/80' : 'text-gray-400'}`}
              >
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={visible}
          loading={loading}
          searchPlaceholder="Search devices…"
          emptyMessage={
            statusFilter === 'pending_approval'
              ? 'No devices awaiting approval'
              : 'No enrolled devices yet'
          }
        />
      </div>

      {/* Approve */}
      <ConfirmDialog
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={handleApprove}
        title="Approve device"
        message={
          <span>
            Approve <strong>{approveTarget?.device_name || approveTarget?.device_id}</strong>? Its certificate becomes
            admissible and the signed roster republishes automatically.
          </span>
        }
        confirmText="Approve"
        variant="info"
        isLoading={approving}
      />

      {/* Delete */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete device record"
        message={
          <span>
            Delete <strong>{deleteTarget?.device_id}</strong>? This removes the enrolment record. To immediately cut a
            connected device, use Revoke instead — it drops live sessions fleet-wide.
          </span>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

      <ReassignDeviceModal
        device={reassignTarget}
        onClose={() => setReassignTarget(null)}
        onDone={async () => {
          setReassignTarget(null);
          // Refetch rather than patching in place: the row comes back
          // pending_approval with its certificate gone, and the status filter
          // chips must recount.
          await fetchDevices();
        }}
      />

      <RevokeDeviceModal
        device={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onDone={async () => {
          setRevokeTarget(null);
          await fetchDevices();
        }}
      />

      <DeviceDetailModal device={detailTarget} onClose={() => setDetailTarget(null)} />
    </div>
  );
};

export default AccessDevices;
